import { Button, Modal, SelectField, TextAreaField, TextField, WriteError } from '@shared/ui';
import { useState } from 'react';

import {
    definitionDraftFrom,
    definitionEditFrom,
    type DefinitionDraft,
} from '../api/definition-edit';
import { useUpdateFlag } from '../hooks/use-flags';
import {
    explainSalesforceFieldRejection,
    FLAG_TYPE_LABEL,
    rejectSalesforceField,
} from '../schemas/flag-type';
import { PURPOSE_OPTIONS } from '../schemas/purpose';

import type { FlagDefinition, FlagLifecycle } from '../schemas/flag';

const LIFECYCLE_OPTIONS: readonly { value: FlagLifecycle; label: string }[] = [
    { value: 'dev', label: 'Dev — not ready for anyone' },
    { value: 'beta', label: 'Beta — selected museums' },
    { value: 'ga', label: 'GA — generally available' },
    { value: 'deprecated', label: 'Deprecated — stop using' },
];

/**
 * Edit a flag's editorial details — `PATCH /api/flags/:key`.
 *
 * The **type is shown, never editable** (ADR 0009 §5). It is part of what the
 * key means: flipping it would strand every consumer of the old meaning without
 * any of them failing, so the API refuses the change with `FLAG_TYPE_IMMUTABLE`
 * and this form does not offer it. The key is not shown as a field either, for
 * the same reason it cannot be renamed.
 *
 * The body carries only what changed — see `definitionEditFrom` — so the save
 * button stays disabled until something has.
 */
export function EditFlagDialog({
    definition,
    open,
    onClose,
}: {
    definition: FlagDefinition;
    open: boolean;
    onClose: () => void;
}) {
    const [draft, setDraft] = useState<DefinitionDraft>(() => definitionDraftFrom(definition));
    const [reason, setReason] = useState('');
    const [draftVersion, setDraftVersion] = useState(definition.version);
    const update = useUpdateFlag(definition.key);

    // A new version under an open form — a save, or a colleague's edit refetched
    // after a 409 — reseeds the draft. Diffed against the new row, an untouched
    // field still holding the old value would read as a change and quietly
    // revert the colleague's edit. Adjusted during render rather than in an
    // effect so the stale draft never paints; the mutation, and its conflict
    // message, survive because nothing remounts.
    if (definition.version !== draftVersion) {
        setDraftVersion(definition.version);
        setDraft(definitionDraftFrom(definition));
    }

    const set = <K extends keyof DefinitionDraft>(field: K, value: DefinitionDraft[K]) => {
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const close = () => {
        setDraft(definitionDraftFrom(definition));
        setReason('');
        update.reset();
        onClose();
    };

    const isSalesforce = definition.type === 'salesforce';
    // A flag saved before NOXSCRUM-1758 still carries the legacy `__c`. Left
    // untouched it is not sent, and the API drops the suffix itself, so only a
    // name the operator changed is held to the rule.
    const fieldTouched = draft.salesforceField.trim() !== (definition.salesforceField ?? '');
    const fieldRejection =
        isSalesforce && fieldTouched ? rejectSalesforceField(draft.salesforceField) : null;
    const trimmedReason = reason.trim();
    const body = definitionEditFrom(
        definition,
        draft,
        trimmedReason.length > 0 ? trimmedReason : undefined,
    );

    const complete =
        body !== null &&
        fieldRejection === null &&
        draft.name.trim().length > 0 &&
        draft.description.trim().length > 0 &&
        draft.category.trim().length > 0 &&
        draft.owner.trim().length > 0;

    return (
        <Modal
            open={open}
            onClose={close}
            dismissable={!update.isPending}
            title={`Edit ${definition.key}`}
            description="Details only. Nothing here turns the flag on or off."
            footer={
                <>
                    <Button variant="ghost" onClick={close} disabled={update.isPending}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!complete || update.isPending}
                        onClick={() => {
                            if (body === null) return;
                            update.mutate(body, { onSuccess: close });
                        }}
                    >
                        {update.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                <TextField
                    label="Type"
                    value={FLAG_TYPE_LABEL[definition.type]}
                    onChange={() => undefined}
                    disabled
                    hint="Fixed at creation. A flag that needs to mean something else is a new key."
                />
                {isSalesforce && (
                    <TextField
                        label="Salesforce field"
                        value={draft.salesforceField}
                        onChange={(value) => {
                            set('salesforceField', value);
                        }}
                        mono
                        autoFocus
                        error={
                            fieldRejection
                                ? explainSalesforceFieldRejection(fieldRejection)
                                : undefined
                        }
                        hint="The V2_Salesforce_Flag__c record this flag's value is projected to. One record belongs to one flag."
                    />
                )}
                <TextField
                    label="Name"
                    value={draft.name}
                    onChange={(value) => {
                        set('name', value);
                    }}
                    autoFocus={!isSalesforce}
                />
                <TextAreaField
                    label="What it gates"
                    value={draft.description}
                    onChange={(value) => {
                        set('description', value);
                    }}
                />
                <div className="grid grid-cols-2 gap-4">
                    <TextField
                        label="Category"
                        value={draft.category}
                        onChange={(value) => {
                            set('category', value);
                        }}
                    />
                    <TextField
                        label="Owning team"
                        value={draft.owner}
                        onChange={(value) => {
                            set('owner', value);
                        }}
                    />
                </div>
                {/* Above Lifecycle, and the pairing is the point: this says
                    whether the flag has an end, that one says how far along it
                    is. Both are editorial; only this one changes what the flag
                    is for. */}
                <SelectField
                    label="Purpose"
                    value={draft.purpose}
                    onChange={(value) => {
                        set('purpose', value);
                    }}
                    options={PURPOSE_OPTIONS}
                    hint="What the flag exists for. Changing it is how a flag built as an experiment becomes one the platform sells — the key stays the same."
                />
                <SelectField
                    label="Lifecycle"
                    value={draft.lifecycle}
                    onChange={(value) => {
                        set('lifecycle', value);
                    }}
                    options={LIFECYCLE_OPTIONS}
                    hint="A label for operators. The switch that actually stops a flag being served is Archived, on the default."
                />
                <TextAreaField
                    label="Reason (optional)"
                    value={reason}
                    onChange={setReason}
                    rows={2}
                    placeholder="Why this changed"
                    hint="Recorded on the audit entry."
                />

                {update.error && <WriteError error={update.error} />}
            </div>
        </Modal>
    );
}
